import React, { useState, useEffect } from 'react';
import { Tooltip, Modal } from 'antd';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CustomService } from '../../../services/api/custom/CustomService';
import { useNavigate } from 'react-router-dom';

// Função segura para formatar datas
const safeFormatDate = (date, pattern = 'dd/MM/yyyy') => {
  if (!date) return '-';
  const parsed = new Date(date);
  return isNaN(parsed.getTime())
    ? '-'
    : format(parsed, pattern, { locale: ptBR });
};

const OrcamentoTable = ({ data = [], loading = false }) => {
  const [loadingDuplicarId, setLoadingDuplicarId] = useState(null);
  const [orcamentos, setOrcamentos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [orcamentoDetalhado, setOrcamentoDetalhado] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [orcamentoSelecionado, setOrcamentoSelecionado] = useState(null);

  useEffect(() => {
    if (Array.isArray(data)) {
      const ordenado = [...data].sort(
        (a, b) => new Date(b.criado_em) - new Date(a.criado_em)
      );
      setOrcamentos(ordenado);
    }
  }, [data]);

  const handleDuplicar = (record) => {
    Modal.confirm({
      title: 'Deseja realmente duplicar este orçamento?',
      content: `Pedido nº ${record.fid_pedido}`,
      okText: 'Duplicar',
      cancelText: 'Cancelar',
      centered: true,
      onOk: async () => {
        setLoadingDuplicarId(record.id); // ativa o loading para este botão
        try {
          const novoOrcamento = await CustomService.postRequest(
            `/interno/orcamento/${record.fid_pedido}/duplicar`
          );

          if (novoOrcamento) {
            setOrcamentos((prev) => [novoOrcamento, ...prev]);
          }
        } catch (error) {
          console.error('Erro ao duplicar orçamento:', error);
        } finally {
          setLoadingDuplicarId(null); // desativa o loading
        }
      },
    });
  };

  const handleVerItens = async (record) => {
    setLoadingDetalhe(true);
    setShowModal(true);
    setOrcamentoSelecionado(record); // 👉 guarda o orçamento original

    try {
      const pedidoResp = await CustomService.getRequest(
        `/interno/pedido/${record.fid_pedido}`
      );

      const itens =
        pedidoResp.items?.map((item) => {
          const precoUnitario = (() => {
            switch (item.tipo_preco) {
              case 'B':
                return (item.preco_b ?? 0) / 100;
              case 'C':
                return (item.preco_c ?? 0) / 100;
              default:
                return (item.preco ?? 0) / 100;
            }
          })();

          return {
            id: item.id,
            nome: item.nome,
            quantidade: item.quantidade,
            preco_unitario: precoUnitario,
            imagem: item.imagem,
          };
        }) ?? [];

      const itensOrdenados = [...itens].sort((a, b) =>
        a.nome.localeCompare(b.nome, 'pt', { sensitivity: 'base' })
      );

      setOrcamentoDetalhado({ items: itensOrdenados });
    } catch (error) {
      console.error('Erro ao buscar pedido:', error);
      setOrcamentoDetalhado(null);
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const navigate = useNavigate();

  const columns = [
    { title: 'Pedido', dataIndex: 'fid_pedido', key: 'fid_pedido' },
    { title: 'Qtd Itens', dataIndex: 'itens', key: 'itens' },
    {
      title: 'Valor Total',
      dataIndex: 'preco_total',
      key: 'preco_total',
      render: (value) => {
        return value.preco_total
          ? `R$ ${Number(value.preco_total / 100)
              .toFixed(2)
              .replace('.', ',')}`
          : 'R$ 0,00';
      },
    },
    {
      title: 'Data',
      dataIndex: 'criado_em',
      key: 'criado_em',
      render: (value) => safeFormatDate(value),
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      render: (_, record) => {
        const statusItems = [
          {
            label: 'Aprovado',
            value: record.aprovado_em,
            color: 'bg-green-100 text-green-700',
            colorPending: 'bg-gray-100 text-gray-600',
            tooltip: record.aprovado_em
              ? `Aprovado em ${safeFormatDate(
                  record.aprovado_em,
                  'dd/MM/yyyy HH:mm'
                )}`
              : 'Pendente',
          },
          {
            label: 'Pago',
            value: record.pago_em,
            color: 'bg-yellow-100 text-yellow-700',
            colorPending: 'bg-gray-100 text-gray-600',
            tooltip: record.pago_em
              ? `Pago em ${safeFormatDate(record.pago_em, 'dd/MM/yyyy HH:mm')}`
              : 'Pendente',
          },
          {
            label: 'Entregue',
            value: record.entregue_em,
            color: 'bg-blue-100 text-blue-700',
            colorPending: 'bg-gray-100 text-gray-600',
            tooltip: record.entregue_em
              ? `Entregue em ${safeFormatDate(
                  record.entregue_em,
                  'dd/MM/yyyy HH:mm'
                )}`
              : 'Pendente',
          },
        ];

        return (
          <div className="flex justify-center gap-2 flex-wrap">
            {statusItems.map((item) => (
              <Tooltip key={item.label} title={item.tooltip}>
                <div
                  className={`px-2 py-[2px] rounded-full text-xs font-semibold cursor-default ${
                    item.value ? item.color : item.colorPending
                  }`}
                >
                  {item.value ? item.label : `Pendente ${item.label}`}
                </div>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Editar',
      key: 'editar',
      align: 'center',
      render: (record) => (
        <button
          className="text-blue-600 hover:text-blue-800 underline"
          onClick={() => navigate(`/gerar-orcamento?id=${record.fid_pedido}`)}
        >
          Editar
        </button>
      ),
    },
    {
      title: 'Ver Itens',
      key: 'ver-itens',
      align: 'center',
      render: (record) => (
        <button
          className="text-green-600 hover:text-green-800 underline"
          onClick={() => handleVerItens(record)}
        >
          Ver Itens
        </button>
      ),
    },
    {
      title: 'Duplicar',
      key: 'duplicar',
      align: 'center',
      render: (record) => (
        <button
          className={`underline ${
            loadingDuplicarId === record.id
              ? 'text-gray-500 cursor-not-allowed'
              : 'text-orange-600 hover:text-orange-800'
          }`}
          onClick={() => loadingDuplicarId === null && handleDuplicar(record)}
          disabled={loadingDuplicarId === record.id}
        >
          {loadingDuplicarId === record.id ? 'Duplicando...' : 'Duplicar'}
        </button>
      ),
    },
  ];

  if (loading)
    return <div className="p-2 text-sm">Carregando orçamentos...</div>;

  if (!data || data.length === 0) {
    return (
      <div className="p-2 text-sm text-gray-500">
        Nenhum orçamento encontrado.
      </div>
    );
  }

  return (
    <>
      <Modal
        title="Itens do Orçamento"
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={700}
      >
        {loadingDetalhe ? (
          <p>Carregando...</p>
        ) : orcamentoDetalhado ? (
          <div className="flex flex-col gap-4">
            {orcamentoDetalhado.items?.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 items-center border-b pb-3 last:border-b-0"
              >
                <img
                  src={item.imagem}
                  alt={item.nome}
                  className="w-16 h-16 rounded shadow object-cover"
                />
                <div className="flex flex-col">
                  <span className="font-semibold text-base">{item.nome}</span>
                  <span className="text-sm text-gray-600">
                    {item.quantidade} x R${' '}
                    {Number(item.preco_unitario).toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
            ))}
            <div className="mb-4 text-sm text-gray-700 space-y-1">
              <div>
                <b>Pedido:</b> {orcamentoSelecionado.fid_pedido}
              </div>
              <div>
                <b>Aprovado em:</b>{' '}
                {safeFormatDate(
                  orcamentoSelecionado.aprovado_em,
                  'dd/MM/yyyy HH:mm'
                )}
              </div>
              <div>
                <b>Pago em:</b>{' '}
                {safeFormatDate(
                  orcamentoSelecionado.pago_em,
                  'dd/MM/yyyy HH:mm'
                )}
              </div>
              <div>
                <b>Entregue em:</b>{' '}
                {safeFormatDate(
                  orcamentoSelecionado.entregue_em,
                  'dd/MM/yyyy HH:mm'
                )}
              </div>
              <div>
                <b>Valor Total Registrado:</b> R${' '}
                {(orcamentoSelecionado.preco_total / 100)
                  .toFixed(2)
                  .replace('.', ',')}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-red-500">Erro ao carregar os itens.</p>
        )}
      </Modal>

      <table className="w-full text-sm border">
        <thead>
          <tr className="bg-gray-100">
            {columns.map((col) => (
              <th key={col.key} className="px-2 py-1 border text-center">
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orcamentos.map((orcamento) => (
            <tr key={orcamento.id}>
              {columns.map((col) => (
                <td key={col.key} className="px-2 py-1 border text-center">
                  {col.render
                    ? col.render(orcamento, orcamento)
                    : orcamento[col.dataIndex] || '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default OrcamentoTable;
